import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import * as XLSX from 'xlsx'
import connexion from '../../connexion'
import { fetchPitches, fetchOpenTour } from '../Inventory/Field/fieldApi'
import { matchesQuery, pitchState, TOUR_STATES } from '../Inventory/Field/fieldTours'
import { canRead } from '../UI/dashboardAccess'
import { locationGroups, interventionsByPitch, openCount, familyLabel, shortFamilyLabel, exportSheets } from './locationsData'
import LocationPanel, { PANEL_WIDTH } from './LocationPanel'

// Page Emplacements (décisions 8 et 45, mise en page de la maquette D5) : tous les emplacements groupés
// par zone ; un clic ouvre la fiche de l'emplacement. Premier temps, demandé par Alexandra le 24/10/2026 :
// interventions, inventaires et tour de terrain par hébergement, sans la vue « Aujourd'hui ».
// Boutons : fond en ligne pour neutraliser Materialize. Tailwind 2.2 sans JIT : styles en ligne.

const COLORS = {
	text: '#0F172A',
	muted: '#475569',
	border: '#E2E8F0',
	primary: '#000095',
	soft: '#E8EBF5'
}

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"

function TourMark({ state })
{
	if (state === 'todo')
		return null
	const look = TOUR_STATES[state]
	return (
		<span title={look.label} aria-hidden="true" style={{ position: 'absolute', top: 6, right: 6, width: 18, height: 18, borderRadius: 999, background: look.background, color: look.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			{state === 'done'
				? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
				: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3z" /></svg>}
		</span>
	)
}

function Tile({ pitch, open, tourState, selected, onPick })
{
	const parts = [pitch.location, familyLabel(pitch)]
	if (open > 0)
		parts.push(open + (open > 1 ? ' interventions ouvertes' : ' intervention ouverte'))
	if (tourState && tourState !== 'todo')
		parts.push(TOUR_STATES[tourState].label + ' dans le tour')
	return (
		<button
			type="button"
			aria-label={parts.join(', ')}
			aria-pressed={selected}
			onClick={() => onPick(pitch)}
			style={{ backgroundImage: 'none', backgroundColor: selected ? '#EEF0FF' : '#FFFFFF', position: 'relative', minHeight: 72, minWidth: 0, borderRadius: 14, border: selected ? '2px solid ' + COLORS.primary : '1px solid ' + COLORS.border, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '8px 6px', color: COLORS.text, cursor: 'pointer', fontFamily: 'inherit' }}
		>
			<span style={{ fontFamily: MONO, fontSize: 19, fontWeight: 700, letterSpacing: '0.02em', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pitch.location}</span>
			<span style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortFamilyLabel(pitch)}</span>
			{tourState && <TourMark state={tourState} />}
			{open > 0 && <span aria-hidden="true" style={{ position: 'absolute', bottom: 6, right: 6, minWidth: 22, height: 20, padding: '0 5px', borderRadius: 6, background: COLORS.text, color: '#FFFFFF', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{open}</span>}
		</button>
	)
}

const chip = (on) => ({
	backgroundImage: 'none',
	backgroundColor: on ? '#1E293B' : '#FFFFFF',
	color: on ? '#FFFFFF' : '#1E293B',
	border: '1px solid ' + (on ? '#1E293B' : '#CBD5E1'),
	minHeight: 40,
	padding: '0 14px',
	borderRadius: 999,
	display: 'inline-flex',
	alignItems: 'center',
	gap: 6,
	fontSize: 14,
	fontWeight: 700,
	cursor: 'pointer',
	fontFamily: 'inherit'
})

export default class LocationsPage extends Component
{
	constructor(props)
	{
		super(props)
		this.state = {
			pitches: props.pitches || null,
			pitchesError: false,
			interventions: [],
			tour: null,
			query: '',
			zone: 'all',
			selected: null
		}
	}

	componentDidMount()
	{
		if (this.props.updateNavbarTitle)
			this.props.updateNavbarTitle('Emplacements')
		// Toujours relus à l'ouverture : un emplacement créé dans le paramétrage apparaît aussitôt
		this.loadPitches()
		fetch(connexion.connect.url + 'dashboard/interventions/list')
			.then(response => response.json())
			.then(list => this.setState({ interventions: Array.isArray(list) ? list : [] }))
			.catch(() => this.setState({ interventions: [] }))
		// Le tour en cours vient du serveur (collection tours)
		fetchOpenTour().then(tour => this.setState({ tour }))
	}

	closePanel = () => this.setState({ selected: null })

	// Export Excel de ce qui est affiché (zone ou recherche) : emplacements, interventions, tour de terrain
	exportExcel = (pitches, byPitch, scopeLabel) => {
		const workbook = XLSX.utils.book_new()
		exportSheets({ pitches, byPitch, tour: this.state.tour, user: this.props.user }).forEach(sheet => {
			const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows, { cellDates: true, dateNF: 'dd/mm/yyyy hh:mm' })
			worksheet['!cols'] = sheet.rows[0].map((title, index) => ({ wch: Math.min(48, Math.max(12, ...sheet.rows.map(row => String(row[index] instanceof Date ? 'jj/mm/aaaa hh:mm' : (row[index] === undefined ? '' : row[index])).length))) }))
			XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name)
		})
		const date = new Date()
		const day = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
		XLSX.writeFile(workbook, 'emplacements_' + scopeLabel + '_' + day + '.xlsx')
	}

	loadPitches = () => {
		this.setState({ pitchesError: false })
		fetchPitches()
			.then(pitches => this.setState(previous => ({
				pitches,
				// Emplacement supprimé ou renommé entre-temps : sa fiche se referme
				selected: previous.selected && pitches.some(pitch => pitch._id === previous.selected._id) ? pitches.find(pitch => pitch._id === previous.selected._id) : null
			})))
			.catch(() => this.setState({ pitchesError: true }))
	}

	render()
	{
		const { pitches, pitchesError, interventions, tour, query, zone, selected } = this.state
		const all = pitches || []
		const groups = locationGroups(all)
		const byPitch = interventionsByPitch(interventions)
		const searching = query.trim() !== ''
		const shown = groups
			.filter(group => searching || zone === 'all' || group.key === zone)
			.map(group => ({ ...group, shown: group.pitches.filter(pitch => matchesQuery(pitch, query)) }))
			.filter(group => group.shown.length > 0)
		const commons = groups.find(group => group.key === 'espaces-communs')

		return (
			<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24, color: COLORS.text, paddingRight: selected ? PANEL_WIDTH : 0 }}>
				<main style={{ flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
					<header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
							<h1 style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.2, margin: 0 }}>Emplacements</h1>
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
							{pitches && (
								<button
									type="button"
									title="Télécharge les emplacements affichés, leurs interventions et le tour de terrain"
									onClick={() => this.exportExcel([].concat(...shown.map(group => group.shown)), byPitch, searching ? 'recherche' : (zone === 'all' ? 'tous' : zone.replace(/\s+/g, '-').toLowerCase()))}
									disabled={shown.length === 0}
									style={{ backgroundImage: 'none', backgroundColor: shown.length === 0 ? '#E2E8F0' : '#FFFFFF', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, padding: '0 14px', borderRadius: 12, border: '1px solid #CBD5E1', color: shown.length === 0 ? COLORS.muted : COLORS.primary, fontSize: 14, fontWeight: 700, cursor: shown.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
								>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M12 4v11M7 10l5 5 5-5" /></svg>
									Exporter en Excel
								</button>
							)}
							{canRead(this.props.user ? this.props.user.permissions : null, 'params') && (
								<Link to="/emplacements/parametrage" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, padding: '0 14px', borderRadius: 12, border: '1px solid #CBD5E1', background: '#FFFFFF', color: COLORS.primary, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
									Gérer les emplacements
								</Link>
							)}
							</div>
						</div>
						<p style={{ fontSize: 16, fontWeight: 500, color: COLORS.muted, margin: 0 }}>
							{pitches ? all.length + ' emplacements' + (commons ? ', dont ' + commons.pitches.length + ' espaces communs' : '') + '. Choisissez un emplacement pour voir ses interventions, ses inventaires et le tour de terrain.' : 'Chargement des emplacements…'}
						</p>
					</header>

					<div style={{ position: 'relative', display: 'flex', alignItems: 'center', maxWidth: 420 }}>
						<label htmlFor="locations-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>Rechercher un numéro d'emplacement</label>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.muted} strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" style={{ position: 'absolute', left: 14 }}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
						<input
							id="locations-search"
							type="search"
							className="browser-default"
							value={query}
							autoComplete="off"
							placeholder="Rechercher un n° (ex : M066)"
							onChange={(e) => this.setState({ query: e.target.value })}
							style={{ width: '100%', minHeight: 44, margin: 0, padding: '0 14px 0 42px', borderRadius: 12, border: '1px solid #CBD5E1', background: '#FFFFFF', fontSize: 16, color: COLORS.text, boxSizing: 'border-box', fontFamily: 'inherit' }}
						/>
					</div>

					{groups.length > 1 && (
						<div role="group" aria-label="Zones" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
							{[{ key: 'all', label: 'Toutes', count: all.length }].concat(groups.map(group => ({ key: group.key, label: group.zone === 'espaces-communs' ? 'Espaces communs' : (group.zone || 'Sans zone'), count: group.pitches.length }))).map(item => {
								const on = !searching && zone === item.key
								return (
									<button key={item.key} type="button" aria-pressed={on} onClick={() => this.setState({ zone: item.key, query: '' })} style={chip(on)}>
										{item.label}
										<span style={{ fontWeight: 500 }}>{'(' + item.count + ')'}</span>
									</button>
								)
							})}
						</div>
					)}

					<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 20px', fontSize: 14, color: '#334155' }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ minWidth: 22, height: 20, padding: '0 5px', borderRadius: 6, background: COLORS.text, color: '#FFFFFF', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>1</span>Interventions ouvertes</span>
						{tour && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, borderRadius: 999, background: TOUR_STATES.done.background }} />{'Fait dans le ' + tour.name}</span>}
						{tour && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 16, height: 16, borderRadius: 999, background: TOUR_STATES.intervention.background }} />Fait avec intervention</span>}
					</div>

					{pitchesError && !pitches && (
						<p style={{ fontSize: 15, color: COLORS.muted }}>
							Impossible de charger les emplacements. Vérifiez la connexion, puis{' '}
							<button type="button" onClick={this.loadPitches} style={{ backgroundImage: 'none', backgroundColor: 'transparent', border: 0, padding: 0, color: COLORS.primary, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 15, fontFamily: 'inherit' }}>réessayez</button>.
						</p>
					)}
					{pitches && shown.length === 0 && <p style={{ fontSize: 15, color: COLORS.muted, padding: '24px 0' }}>{'Aucun emplacement ne correspond à « ' + query.trim() + ' ».'}</p>}

					{shown.map(group => (
						<section key={group.key} aria-label={group.label} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.border, borderRadius: 20, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
							<div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
								<h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{group.label}</h2>
								<p style={{ fontSize: 14, color: COLORS.muted, margin: 0 }}>{group.shown.length + (group.shown.length > 1 ? ' emplacements' : ' emplacement')}</p>
							</div>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
								{group.shown.map(pitch => (
									<Tile
										key={pitch._id || pitch.location}
										pitch={pitch}
										open={openCount(byPitch[String(pitch._id)])}
										tourState={tour ? pitchState(tour, pitch.location) : null}
										selected={!!selected && selected.location === pitch.location}
										onPick={(chosen) => this.setState({ selected: chosen })}
									/>
								))}
							</div>
						</section>
					))}
				</main>
				{selected && (
					<LocationPanel
						key={selected.location}
						pitch={selected}
						interventions={byPitch[String(selected._id)] || []}
						tour={tour}
						user={this.props.user}
						history={this.props.history}
						onClose={this.closePanel}
					/>
				)}
			</div>
		)
	}
}
